/**
 * PhaseGate · route-level gate for Phase 2 (and per-feature) routes.
 *
 * Two modes:
 *   1. Feature-level: <PhaseGate feature="email_ai">...</PhaseGate>
 *      Uses usePhaseFeature(feature) so per-tenant feature_overrides win.
 *   2. Phase-level (legacy): <PhaseGate allow="phase2">...</PhaseGate>
 *      Entire phase blocked for Phase 1 tenants.
 *
 * Admins bypass by default (allowAdmin=true) so Jack/Louie can preview.
 * On deny: navigate to redirectTo with toast. Default redirect is "/dash".
 */
import { ReactNode, useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { usePhase } from "@/hooks/usePhase";
import { usePhaseFeature } from "@/hooks/usePhaseFeature";
import { useRequireRole } from "@/hooks/useRequireRole";
import { toast } from "sonner";
import type { PhaseFeature } from "@/lib/feature-flags";

interface PhaseGateProps {
  /** Feature-level gate (preferred). When set, allow/deny comes from usePhaseFeature. */
  feature?: PhaseFeature;
  /** Legacy phase-level gate. Ignored when `feature` is provided. */
  allow?: "phase1" | "phase2" | "any";
  /** Where to send blocked users. Defaults to "/dash". */
  redirectTo?: string;
  /** If true, platform admins can bypass. Defaults to true. */
  allowAdmin?: boolean;
  /** Toast message on deny. Defaults to a generic "not available" message. */
  denyMessage?: string;
  children: ReactNode;
}

export function PhaseGate({
  feature,
  allow = "phase2",
  redirectTo = "/dash",
  allowAdmin = true,
  denyMessage,
  children,
}: PhaseGateProps) {
  const { phase, isLoading: phaseLoading } = usePhase();
  const { hasRole: isAdmin, loading: adminLoading } = useRequireRole("admin");
  const location = useLocation();
  const featureResolution = usePhaseFeature(feature ?? ("load_board" as PhaseFeature));
  const toastedRef = useRef(false);

  // Admin override — preview anything
  const adminBypass = allowAdmin && isAdmin;

  // Feature-level gate takes precedence
  if (feature) {
    // While loading, render nothing (prevents flash of gated content).
    if (featureResolution.isLoading) return null;
    if (adminBypass || featureResolution.enabled) return <>{children}</>;
    // Don't bounce+toast a platform admin whose role RPC is still resolving
    // (2026-07-14 Wave 2 nit; fail-closed either way — nothing renders yet).
    if (allowAdmin && adminLoading) return null;
  } else {
    if (allow === "any") return <>{children}</>;
    if (adminBypass) return <>{children}</>;
    // Wait for the authoritative DB phase before gating, so a stale-localStorage
    // Phase 2 value can't flash gated content for a Phase 1 tenant.
    if (phaseLoading) return null;
    if (phase === allow) return <>{children}</>;
    if (allowAdmin && adminLoading) return null;
  }

  return (
    <GateDeny
      redirectTo={redirectTo}
      denyMessage={denyMessage}
      from={location.pathname}
      toastedRef={toastedRef}
    />
  );
}

function GateDeny({
  redirectTo,
  denyMessage,
  from,
  toastedRef,
}: {
  redirectTo: string;
  denyMessage?: string;
  from: string;
  toastedRef: React.MutableRefObject<boolean>;
}) {
  useEffect(() => {
    if (toastedRef.current) return;
    toastedRef.current = true;
    toast.message(denyMessage ?? "That feature isn't available on your current plan yet.");
  }, [denyMessage, toastedRef]);

  return <Navigate to={redirectTo} replace state={{ from }} />;
}

export default PhaseGate;
